import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { User } from "./types";
import { clearPersistedCache } from "./query-client";

export const BASE_URL =
  import.meta.env.VITE_API_URL ?? "https://api.yatbooks.com";
const ACTIVE_ORG_STORAGE_KEY = "active_org_id";

export const api = axios.create({
  baseURL: BASE_URL,
});

export const uninterceptedApi = axios.create({
  baseURL: BASE_URL,
});

export function decodeJWT(token: string): User | null {
  try {
    const decoded = jwtDecode<User>(token);
    return decoded;
  } catch (error) {
    console.error(error);
    return null;
  }
}

export function getAccessToken(): string | null {
  return localStorage.getItem("access_token");
}

export function getRefreshToken(): string | null {
  return localStorage.getItem("refresh_token");
}

export function getActiveOrganizationId(): string | null {
  return localStorage.getItem(ACTIVE_ORG_STORAGE_KEY);
}

export function setActiveOrganizationId(organizationId: string): void {
  localStorage.setItem(ACTIVE_ORG_STORAGE_KEY, organizationId);
}

export function clearActiveOrganizationId(): void {
  localStorage.removeItem(ACTIVE_ORG_STORAGE_KEY);
}

export function applyActiveOrganizationToUser(
  user: User,
  organizationId?: string | null
): User {
  if (!organizationId) {
    return user;
  }

  return {
    ...user,
    org_id: organizationId,
  };
}

export function getCurrentUserFromStorage(): User | null {
  const token = getAccessToken();
  if (!token) {
    return null;
  }

  const user = decodeJWT(token);
  if (!user) {
    return null;
  }

  return applyActiveOrganizationToUser(user, getActiveOrganizationId());
}

export function isTokenExpired(): boolean {
  const expiry = localStorage.getItem("token_expiry");
  if (!expiry) return true;
  return Date.now() > Number(expiry) * 1000;
}

/** End the session on this device: tokens, active org and every cached query (memory + IndexedDB). */
export async function clearTokens(): Promise<void> {
  localStorage.removeItem("access_token");
  localStorage.removeItem("refresh_token");
  localStorage.removeItem("token_expiry");
  localStorage.removeItem("user");
  clearActiveOrganizationId();
  await clearPersistedCache();
}

api.interceptors.request.use(
  async (config) => {
    if (isTokenExpired()) {
      if (!navigator.onLine) {
        return Promise.reject(new Error("Offline: Cannot refresh token"));
      }

      const refreshToken = getRefreshToken();

      if (refreshToken) {
        try {
          const response = await uninterceptedApi.post("/auth/refresh", {
            refreshToken,
          });
          const { accessToken, expiresIn } = response.data;
          localStorage.setItem("access_token", accessToken);
          localStorage.setItem(
            "token_expiry",
            ((Date.now() + expiresIn * 1000) / 1000).toString()
          );
        } catch (error) {
          await clearTokens();
          window.location.href = "/login";
          return Promise.reject(error);
        }
      } else {
        await clearTokens();
        window.location.href = "/login";
        return Promise.reject("No refresh token available");
      }
    }

    const token = getAccessToken();

    if (token) {
      const userInfo = decodeJWT(token);
      config.headers["Authorization"] = `Bearer ${token}`;
      const activeOrganizationId =
        getActiveOrganizationId() ?? userInfo?.org_id;
      if (activeOrganizationId) {
        config.headers["X-Organization-Id"] = activeOrganizationId;
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);
